package com.zi.support.shared

/**
 * More > Device management on web, Android and iOS: every app record (Support App, Driver Hub, Operations App,
 * Heating App, Attendance App, CargoView), grouped into physical phones by company. Each app sees its own ANDROID_ID on the same
 * phone, and a factory reset gives new ones, so nothing links them automatically: an IMEI typed in by support does.
 * deviceRegistry/{imei} holds the phone's name and the record keys linked to it.
 */
object DeviceRegistry {
    val companies: List<String> = DriverLogs.companies

    /** One app's record for a phone. [key] ("Driver Hub|driverHubDevices/abc…") is what deviceRegistry links. */
    data class AppRecord(
        val app: String,
        val key: String,
        /** The app's own id for the phone: Driver Hub's ANDROID_ID, the Support App's device id… */
        val deviceId: String,
        val company: String,
        /** Who is logged in there: the driver, operator or support user ("Not logged in" when nobody). */
        val user: String,
        val model: String,
        /** The name set in the phone's settings, e.g. "KPH Truck 12" ("" when the app doesn't send it). */
        val phoneName: String,
        val platform: String,
        val version: String,
        val lastOpenedMillis: Long,
        val server: String,
        /** Every field of the app's record (credentials already dropped), for the phone page. */
        val details: List<DeviceDetail> = emptyList(),
        /** The record's updatedAt: for the Support App, its 15-minute check-in, which decides Online. */
        val lastSeenMillis: Long = 0,
        /** The install's number: 10000… Support App, 20000… Driver Hub, 30000… Operations, 40000… Heating, 50000… Attendance ("" until assigned). */
        val number: String = "",
    )

    /** A registry entry: the IMEI, the name support gave the phone, and the records linked to it. */
    data class Registered(val imei: String, val name: String, val links: List<String>)

    /** A physical phone: its records from every app (newest first); [imei] is "" until support adds one. */
    data class Phone(
        val id: String,
        val imei: String,
        val name: String,
        val company: String,
        val records: List<AppRecord>,
        val lastOpenedMillis: Long,
    )

    /**
     * An app record from More > Apps' [device] ([AppInstalls.device] or [AppInstalls.centreDevice]); [recordPath] is its
     * Firestore path or NotificationCentre path, unique per app.
     */
    fun record(app: String, recordPath: String, device: SupportDevice): AppRecord {
        val fields = device.details.associate { it.label to it.value }
        val company = when (app) {
            AppInstalls.DRIVER_HUB -> DriverLogs.classify(device.server, device.owner).first
            AppInstalls.SUPPORT_APP -> DriverLogs.OTHER
            // NotificationCentre paths: "operations-app/kph/devices/{id}"; owner is that company slug.
            else -> DriverLogs.classify("", device.owner.replace('-', ' ')).first
        }
        val user = when (app) {
            AppInstalls.SUPPORT_APP -> device.owner.substringBefore('@')
            else -> device.deviceId
        }.ifBlank { DriverLogs.NOT_LOGGED_IN }
        val id = fields["installationId"] ?: fields["deviceId"] ?: recordPath.substringAfterLast('/')
        return AppRecord(app, "$app|$recordPath", id, company, user, device.model, fields["deviceName"].orEmpty(), device.platform,
            device.appVersion, maxOf(device.lastOpenedMillis, 0), device.server, device.details, device.lastSeenMillis,
            device.number.ifBlank { if (app == AppInstalls.SUPPORT_APP) device.deviceId else "" })
    }

    /** Every phone, most recently opened first: records sharing an IMEI are one phone; the rest are a phone each. */
    fun phones(records: List<AppRecord>, registry: List<Registered>): List<Phone> {
        val imeiOf = registry.flatMap { entry -> entry.links.map { it to entry } }.toMap()
        return records.groupBy { imeiOf[it.key]?.imei ?: it.key }.map { (id, group) ->
            val newest = group.sortedByDescending { it.lastOpenedMillis }
            val entry = imeiOf[newest.first().key]
            Phone(id, entry?.imei.orEmpty(),
                entry?.name?.ifBlank { null } ?: newest.firstNotNullOfOrNull { it.phoneName.ifBlank { null } } ?: newest.first().model.ifBlank { "Unknown phone" },
                newest.first().company, newest, newest.first().lastOpenedMillis)
        }.sortedByDescending { it.lastOpenedMillis }
    }

    /** A company tab's phones matching [query] (IMEI, name, model, user, app or device id). */
    fun shown(phones: List<Phone>, company: String, query: String): List<Phone> {
        val needle = query.trim()
        return phones.filter { phone ->
            phone.company == company && (needle.isEmpty() || (listOf(phone.imei, phone.name) +
                phone.records.flatMap { listOf(it.app, it.user, it.model, it.deviceId, it.number, it.phoneName) }).any { it.contains(needle, ignoreCase = true) })
        }
    }

    /** The install's number (e.g. "20004"); before it has one, the last six characters of the app's id for the phone. */
    fun shortId(record: AppRecord): String = record.number.ifBlank { record.deviceId.takeLast(6) }

    /** Digits only; a real IMEI is 15 digits with a valid Luhn check digit (the last one). */
    fun cleanImei(text: String): String = text.filter { it.isDigit() }

    fun isValidImei(text: String): Boolean {
        val digits = cleanImei(text)
        if (digits.length != 15 || digits.length != text.trim().replace(" ", "").replace("-", "").length) return false
        val sum = digits.reversed().mapIndexed { i, c ->
            val d = c - '0'
            if (i % 2 == 1) (d * 2).let { if (it > 9) it - 9 else it } else d
        }.sum()
        return sum % 10 == 0
    }

    /**
     * "Online" / "Offline" for Support App records, the same rule as the old Devices tab ([DeviceStatus.ONLINE_WINDOW_MILLIS]);
     * null for the other apps, which only check in when opened.
     */
    fun presence(record: AppRecord, nowMillis: Long): String? = if (record.app != AppInstalls.SUPPORT_APP) null
        else if (record.lastSeenMillis > 0 && nowMillis - record.lastSeenMillis <= DeviceStatus.ONLINE_WINDOW_MILLIS) "Online" else "Offline"

    /** "Opened 5 min ago" / "Never opened". */
    fun openedLabel(record: AppRecord, nowMillis: Long): String =
        if (record.lastOpenedMillis > 0) "Opened ${DeviceStatus.ago(record.lastOpenedMillis, nowMillis)}" else "Never opened"

    /** The phone page's summary: IMEI, the name in its settings, model, platform, apps and the last time any was opened. */
    fun phoneFacts(phone: Phone, nowMillis: Long): List<DeviceDetail> {
        val newest = phone.records.first()
        return listOf(
            DeviceDetail("IMEI", phone.imei.ifEmpty { "Not added yet" }),
            DeviceDetail("Phone name", phone.records.firstNotNullOfOrNull { it.phoneName.ifBlank { null } } ?: "Not reported"),
            DeviceDetail("Model", phone.records.firstNotNullOfOrNull { it.model.ifBlank { null } } ?: "Unknown"),
            DeviceDetail("Platform", phone.records.firstNotNullOfOrNull { it.platform.ifBlank { null } }?.replaceFirstChar { it.uppercase() } ?: "Unknown"),
            DeviceDetail("Apps", phone.records.map { it.app }.distinct().joinToString(", ")),
            DeviceDetail("Last opened", if (newest.lastOpenedMillis > 0) "${DeviceStatus.ago(newest.lastOpenedMillis, nowMillis)} · ${newest.app}" else "Never"),
            DeviceDetail("Company", phone.company),
        )
    }

    /** One app's section on the phone page: its id for the phone, who is logged in, version, last opened, server. */
    fun recordFacts(record: AppRecord, nowMillis: Long): List<DeviceDetail> = listOfNotNull(
        record.number.ifBlank { null }?.let { DeviceDetail("Device number", it) },
        DeviceDetail("Device ID", record.deviceId),
        DeviceDetail("Logged in", record.user),
        DeviceDetail("Version", record.version.ifBlank { "Unknown" }),
        DeviceDetail("Last opened", if (record.lastOpenedMillis > 0) DeviceStatus.ago(record.lastOpenedMillis, nowMillis) else "Never"),
        record.server.ifBlank { null }?.let { DeviceDetail("Server", DeviceStatus.serverLabel(it)) },
        DeviceDetail("Company", record.company),
    )

    /** "3 apps · last opened 5 min ago" under a phone's name. */
    fun summary(phone: Phone, nowMillis: Long): String =
        listOf("${phone.records.size} app record${if (phone.records.size == 1) "" else "s"}",
            if (phone.lastOpenedMillis > 0) "last opened ${DeviceStatus.ago(phone.lastOpenedMillis, nowMillis)}" else "never opened").joinToString(" · ")
}
